import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../api/client";
import Icon from "../components/Icon";
import { Avatar } from "../components/ui";
import { useAuth } from "../store/auth";
import { useToast } from "../store/toast";
import { fmtStamp } from "../utils";

// Selector de emojis para los mensajes (contenido del usuario, tipo chat).
const EMOJIS = "😀 😃 😄 😁 😉 😊 😍 😎 🤗 🤔 😅 😂 🙂 😐 😕 😮 😢 😭 😴 🤒 👍 👎 👌 🙏 👏 💪 🙌 🤝 ✅ ❌ ⚠️ ❗ ❓ ❤️ 🔥 ⭐ 💯 💡 🎉 📌 📎 🕐 🔴 🟢 🟡 🔵 ➡️ ⬆️".split(" ");
function EmojiPicker({ onPick }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button type="button" className="btn-ghost px-2" title="Emoji" onClick={() => setOpen((o) => !o)}>
        <Icon name="smile" size={18} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute bottom-full left-0 z-30 mb-1 grid max-h-44 w-60 grid-cols-8 gap-0.5 overflow-auto rounded-lg border border-slate-200 bg-white p-1.5 shadow-lg">
            {EMOJIS.map((e) => (
              <button key={e} type="button" className="rounded p-1 text-lg leading-none hover:bg-slate-100"
                onClick={() => { onPick(e); setOpen(false); }}>{e}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Mensajería centralizada (Fase mensajería): consolida todos los chats de tareas
// y proyectos como canales tipo WhatsApp. Cada tarea/proyecto es un canal cuyo
// título es su nombre; desde aquí sigues el hilo, buscas y respondes.

const isImg = (n) => /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(n || "");
const CONVO_ICON = { task: "check", project: "folder", dm: "user", group: "users" };

// Carga una imagen (con auth) desde una URL y la muestra en línea.
function BlobImg({ url, name }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let alive = true, made = "";
    api.get(url, { responseType: "blob" })
      .then((r) => { if (alive) { made = URL.createObjectURL(r.data); setSrc(made); } }).catch(() => {});
    return () => { alive = false; if (made) URL.revokeObjectURL(made); };
  }, [url]);
  return src
    ? <img src={src} alt={name} className="max-h-52 max-w-[240px] rounded-lg border border-slate-200 object-contain" />
    : <span className="text-[11px] text-slate-400">cargando imagen…</span>;
}

function PendingImage({ file }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    const url = URL.createObjectURL(file); setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return src ? <img src={src} alt="Vista previa del adjunto" className="h-10 w-10 rounded object-cover" /> : null;
}

function FileChip({ url, name, image, downloadUrl = url }) {
  const { push } = useToast();
  const open = async () => {
    // Abrir una pestaña sincrónicamente evita que el navegador bloquee la
    // ventana cuando la descarga autenticada termina de resolverse.
    const tab = window.open("about:blank", "_blank");
    try {
      const r = await api.get(url, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      if (tab) tab.location.href = u;
      else {
        const a = document.createElement("a"); a.href = u; a.download = name;
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(u), 60000);
    } catch (e) { if (tab) tab.close(); push(e.response?.data?.detail || "No se pudo abrir el archivo. Comprueba tu acceso e inténtalo de nuevo.", "error"); }
  };
  const download = async () => {
    try {
      const r = await api.get(downloadUrl, { params: { download: true }, responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = u; a.download = name;
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(u), 60000);
    } catch (e) { push(e.response?.data?.detail || "No se pudo descargar el archivo. Comprueba tu acceso e inténtalo de nuevo.", "error"); }
  };
  if (image) return (
    <span className="my-1 inline-flex items-end gap-1">
      <button type="button" onClick={open} aria-label={`Previsualizar ${name}`}><BlobImg url={url} name={name} /></button>
      <button type="button" onClick={download} title={`Descargar ${name}`} aria-label={`Descargar ${name}`} className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-brand-700"><Icon name="download" size={14} /></button>
    </span>
  );
  return (
    <span className="my-0.5 inline-flex max-w-full items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600">
      <button type="button" onClick={open} className="inline-flex min-w-0 items-center gap-1 hover:text-brand-700"><Icon name="paperclip" size={12} /><span className="truncate">{name}</span></button>
      <button type="button" onClick={download} title={`Descargar ${name}`} aria-label={`Descargar ${name}`} className="shrink-0 rounded p-1 hover:bg-slate-100 hover:text-brand-700"><Icon name="download" size={13} /></button>
    </span>
  );
}

// Renderiza el cuerpo con marcadores de archivo de tarea ([[file:...]]) y de
// chat genérico ([[cfile:...]]), con imágenes en línea.
function LinkChip({ to, icon, label }) {
  const nav = useNavigate();
  return (
    <button type="button" onClick={() => nav(to)} className="mx-0.5 inline-flex items-center gap-1 rounded-tag bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 hover:bg-brand-100">
      <Icon name={icon} size={11} />{label}
    </button>
  );
}

function renderBody(body, taskId) {
  const parts = String(body || "").split(/(\[\[(?:c?file|proj|task|req):\d+:[^\]]+\]\])/g);
  return parts.map((p, i) => {
    const mp = p.match(/^\[\[proj:(\d+):(.+)\]\]$/);
    if (mp) return <LinkChip key={i} to={`/proyectos?p=${mp[1]}&view=chat`} icon="folder" label={mp[2]} />;
    const mtk = p.match(/^\[\[task:(\d+):(.+)\]\]$/);
    if (mtk) return <LinkChip key={i} to={`/?task=${mtk[1]}`} icon="check" label={mtk[2]} />;
    const mr = p.match(/^\[\[req:(\d+):(.+)\]\]$/);
    if (mr) return <LinkChip key={i} to={`/compras/requisiciones/${mr[1]}`} icon="fileText" label={mr[2]} />;
    const mt = p.match(/^\[\[file:(\d+):(.+)\]\]$/);
    if (mt) {
      const [, id, name] = mt;
      if (!taskId) return <span key={i} className="text-xs text-slate-400">📎 {name}</span>;
      return <FileChip key={i} url={`/tasks/${taskId}/attachments/${id}/download?view=true`} downloadUrl={`/tasks/${taskId}/attachments/${id}/download`} name={name} image={isImg(name)} />;
    }
    const mc = p.match(/^\[\[cfile:(\d+):(.+)\]\]$/);
    if (mc) {
      const [, id, name] = mc;
      return <FileChip key={i} url={`/messaging/files/${id}`} name={name} image={isImg(name)} />;
    }
    return <span key={i} className="whitespace-pre-wrap break-words">{p}</span>;
  });
}

export function Thread({ convo, onSent, onBack, targetMessageId }) {
  const { user } = useAuth();
  const { push } = useToast();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const messagesRef = useRef(new Map());
  const lastMessageRef = useRef(null);

  const cid = convo.conversation_id || convo.id;

  const load = useCallback((silent = false) => {
    if (!convo) return;
    if (!silent) setLoading(true);
    let req;
    if (convo.type === "task") {
      req = api.get(`/tasks/${convo.id}`).then((r) => (r.data.comments || []));
    } else if (convo.type === "project") {
      req = api.get(`/projects/${convo.id}/comments`).then((r) => r.data || []);
    } else req = api.get(`/messaging/threads/${cid}/messages`).then((r) => r.data.messages || []);
    req.then((list) => setMsgs(list)).catch(() => setMsgs([])).finally(() => setLoading(false));
  }, [convo, cid]);
  useEffect(load, [load]);
  useEffect(() => {
    const timer = setInterval(() => load(true), 8000);
    const onNotification = (e) => {
      const n = e.detail;
      if (n?.origin === "chat" && (n.link || "").includes(`c=${cid}`)) load();
    };
    window.addEventListener("tf:notification", onNotification);
    return () => { clearInterval(timer); window.removeEventListener("tf:notification", onNotification); };
  }, [load, cid]);
  useEffect(() => {
    const params = convo.type === "task" ? { task_id: convo.id }
      : convo.type === "project" ? { project_id: convo.id }
      : { conversation_id: cid };
    api.post("/notifications/read-chat", null, { params })
      .then(() => window.dispatchEvent(new Event("tf:notifications-refresh"))).catch(() => {});
  }, [convo.type, convo.id, cid]);
  useEffect(() => {
    const target = targetMessageId && messagesRef.current.get(String(targetMessageId));
    if (target) { target.scrollIntoView({ block: "center", behavior: "smooth" }); target.classList.add("ring-2", "ring-amber-400"); }
    else {
      const latest = msgs[msgs.length - 1]?.id ?? null;
      if (latest !== lastMessageRef.current) endRef.current?.scrollIntoView();
      lastMessageRef.current = latest;
    }
  }, [msgs, targetMessageId]);

  // # → proyectos, * → tareas, ! → requisiciones: autocompletado para compartir el enlace.
  const [linkKind, setLinkKind] = useState(null);   // "proj" | "task" | "req" | null
  const [linkSug, setLinkSug] = useState([]);
  useEffect(() => {
    const mention = text.match(/(^|\s)@([\wáéíóúñ._-]*)$/i);
    if (mention && convo.type !== "dm" && convo.type !== "group") {
      setLinkKind("user");
      const q = mention[2].toLocaleLowerCase();
      const t = setTimeout(() => api.get("/users").then((r) => setLinkSug((r.data || []).filter((u) => u.is_active && u.id !== user?.id && u.full_name.toLocaleLowerCase().includes(q)).slice(0, 8).map((u) => ({ id: u.id, name: u.full_name, handle: (u.email || "").split("@")[0] || u.full_name.replace(/\s+/g, "") })))).catch(() => setLinkSug([])), 200);
      return () => clearTimeout(t);
    }
    const m = text.match(/(^|\s)([#*!])([\wáéíóúñ-]*)$/i);
    if (!m) { setLinkKind(null); setLinkSug([]); return; }
    const kind = m[2] === "#" ? "proj" : m[2] === "*" ? "task" : "req";
    setLinkKind(kind);
    const q = m[3];
    const t = setTimeout(() => {
      const url = kind === "proj" ? "/messaging/link/projects"
        : kind === "task" ? "/messaging/link/tasks" : "/messaging/link/requisiciones";
      api.get(url, { params: { q } }).then((r) => setLinkSug(r.data || [])).catch(() => setLinkSug([]));
    }, 200);
    return () => clearTimeout(t);
  }, [text, user?.id, convo.type]);
  const pickLink = (item) => {
    if (linkKind === "user") {
      setText((prev) => prev.replace(/@[\wáéíóúñ._-]*$/i, `@${item.handle} `));
      setLinkKind(null); setLinkSug([]); return;
    }
    const marker = linkKind === "proj"
      ? `[[proj:${item.id}:${item.name}]] `
      : linkKind === "task"
      ? `[[task:${item.id}:${(item.code ? item.code + " " : "") + item.title}]] `
      : `[[req:${item.id}:${(item.numero ? item.numero + " " : "") + item.descripcion}]] `;
    setText((prev) => prev.replace(/([#*!])([\wáéíóúñ-]*)$/i, marker));
    setLinkKind(null); setLinkSug([]);
  };

  // Editar / borrar mensajes propios. La URL base del mensaje depende del canal.
  const [editing, setEditing] = useState(null);   // {id, text}
  const msgUrl = (id) => convo.type === "task" ? `/tasks/${convo.id}/comments/${id}`
    : convo.type === "project" ? `/projects/${convo.id}/comments/${id}`
    : `/messaging/threads/${cid}/messages/${id}`;
  const saveEdit = async () => {
    const body = (editing.text || "").trim();
    if (!body) return;
    try { await api.patch(msgUrl(editing.id), { body }); setEditing(null); load(); onSent?.(); }
    catch (e) { push(e.response?.data?.detail || "No se pudo editar", "error"); }
  };
  const removeMsg = async (id) => {
    if (!window.confirm("¿Eliminar este mensaje? Quedará marcado como eliminado.")) return;
    try { await api.delete(msgUrl(id)); load(); onSent?.(); }
    catch (e) { push(e.response?.data?.detail || "No se pudo eliminar", "error"); }
  };

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t && !pending) return;
    setSending(true);
    try {
      let body = t;
      if (pending) {
        const fd = new FormData(); fd.append("file", pending);
        if (convo.type === "task") {
          // En tareas el archivo también entra en sus Adjuntos.
          const { data: att } = await api.post(`/tasks/${convo.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" }, onUploadProgress: (e) => setUploadProgress(e.total ? Math.round(e.loaded * 100 / e.total) : 0) });
          body = `${t ? t + " " : ""}[[file:${att.id}:${att.filename}]]`;
        } else {
          const { data: cf } = await api.post("/messaging/files", fd, { headers: { "Content-Type": "multipart/form-data" }, onUploadProgress: (e) => setUploadProgress(e.total ? Math.round(e.loaded * 100 / e.total) : 0) });
          body = `${t ? t + " " : ""}[[cfile:${cf.id}:${cf.filename}]]`;
        }
      }
      let url;
      if (convo.type === "task") url = `/tasks/${convo.id}/comments`;
      else if (convo.type === "project") url = `/projects/${convo.id}/comments`;
      else url = `/messaging/threads/${cid}/messages`;
      await api.post(url, { body });
      setText(""); setPending(null); setUploadProgress(0); load(); onSent?.();
    } catch (e) { push(e.response?.data?.detail || "No se pudo enviar el mensaje. Revisa tu conexión e inténtalo de nuevo.", "error"); }
    finally { setSending(false); setUploadProgress(0); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2.5 sm:px-4">
        {onBack && <button type="button" onClick={onBack} className="btn-ghost -ml-1 px-2 md:hidden" aria-label="Volver a conversaciones"><Icon name="chevron-left" size={18} /></button>}
        <Icon name={CONVO_ICON[convo.type] || "message"} size={18} className="text-brand-500" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-bold text-slate-800">{convo.code ? `${convo.code} · ` : ""}{convo.title}</div>
          <div className="text-[11px] text-slate-400">{convo.subtitle}</div>
        </div>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto bg-slate-50/60 px-4 py-3">
        {loading ? <p className="py-8 text-center text-sm text-slate-400">Cargando…</p>
          : msgs.length === 0 ? <p className="py-8 text-center text-sm text-slate-400">Sin mensajes todavía.</p>
          : msgs.map((c) => {
            const mine = c.author?.id === user?.id || c.author_id === user?.id;
            const isEditing = editing?.id === c.id;
            const deleted = c.deleted || !!c.deleted_at;   // tarea usa deleted_at; proyecto/DM usan deleted
            return (
              <div key={c.id} ref={(el) => { if (el) messagesRef.current.set(String(c.id), el); else messagesRef.current.delete(String(c.id)); }} className={`group flex items-center gap-1.5 ${mine ? "justify-end" : "justify-start"}`}>
                {/* Acciones (solo mensajes propios, no eliminados) a la izquierda de la burbuja */}
                {mine && !deleted && !isEditing && (
                  <div className="flex shrink-0 gap-0.5 opacity-100 transition sm:opacity-0 sm:group-hover:opacity-100">
                    <button title="Editar" onClick={() => setEditing({ id: c.id, text: c.body })} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"><Icon name="edit" size={13} /></button>
                    <button title="Eliminar" onClick={() => removeMsg(c.id)} className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-500"><Icon name="trash" size={13} /></button>
                  </div>
                )}
                <div className={`min-w-0 max-w-[75%] break-words [overflow-wrap:anywhere] rounded-2xl px-3 py-2 text-sm shadow-sm ${deleted ? "bg-slate-100 text-slate-400 italic" : mine ? "bg-brand-600 text-white" : "bg-white text-slate-800"}`}>
                  {!mine && !deleted && <div className="mb-0.5 flex items-center gap-1 text-[11px] font-semibold text-brand-600"><Avatar name={c.author?.full_name || "?"} size={16} />{c.author?.full_name}</div>}
                  {deleted ? (
                    <div className="flex items-center gap-1 text-[13px]"><Icon name="ban" size={12} /> Mensaje eliminado</div>
                  ) : isEditing ? (
                    <div className="flex flex-col gap-1">
                      <textarea autoFocus rows={2} className="w-64 rounded p-1 text-sm text-slate-800 bg-relleno focus:bg-white"
                        value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} />
                      <div className="flex justify-end gap-1">
                        <button onClick={() => setEditing(null)} className="rounded px-2 py-0.5 text-[11px] text-white/80 hover:bg-white/10">Cancelar</button>
                        <button onClick={saveEdit} className="rounded bg-white px-2 py-0.5 text-[11px] font-semibold text-brand-700">Guardar</button>
                      </div>
                    </div>
                  ) : (
                    <div className={mine ? "[&_a]:text-white" : ""}>{renderBody(c.body, convo.type === "task" ? convo.id : null)}</div>
                  )}
                  {!deleted && !isEditing && (
                    <div className={`mt-0.5 text-[11px] ${mine ? "text-white/70" : "text-slate-400"}`}>{fmtStamp(c.created_at)}{c.edited_at ? " · editado" : ""}</div>
                  )}
                </div>
              </div>
            );
          })}
        <div ref={endRef} />
      </div>

      {pending && (
        <div className="flex items-center gap-2 border-t border-brand-100 bg-brand-50/60 px-3 py-1.5 text-xs">
          {isImg(pending.name) ? <PendingImage file={pending} /> : <Icon name="paperclip" size={14} className="text-brand-500" />}
          <span className="min-w-0 flex-1 truncate">{pending.name} <span className="text-slate-400">· {(pending.size / (1024 * 1024)).toFixed(2)} MB</span></span>
          {sending && <span className="shrink-0 text-[11px] text-brand-600">{uploadProgress ? `${uploadProgress}%` : "Preparando…"}</span>}
          <button className="btn-ghost px-1 text-rose-500" onClick={() => setPending(null)}><Icon name="x" size={13} /></button>
        </div>
      )}
      <form onSubmit={send} className="relative flex items-center gap-2 border-t border-slate-200 px-3 py-2">
        {linkKind && linkSug.length > 0 && (
          <div className="absolute bottom-12 left-2 z-20 max-h-56 w-72 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
            <div className="border-b border-slate-100 px-3 py-1 text-[11px] font-bold uppercase text-slate-400">
              {linkKind === "user" ? "Personas (@)" : linkKind === "proj" ? "Proyectos (#)" : linkKind === "task" ? "Tareas (*)" : "Requisiciones (!)"}
            </div>
            {linkSug.map((it) => (
              <button key={it.id} type="button" onClick={() => pickLink(it)} className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50">
                <Icon name={linkKind === "user" ? "user" : linkKind === "proj" ? "folder" : linkKind === "task" ? "check" : "fileText"} size={14} className="text-brand-500" />
                <span className="min-w-0 flex-1 truncate">
                  {linkKind === "user" ? it.name : linkKind === "proj" ? it.name
                    : linkKind === "task" ? `${it.code ? it.code + " · " : ""}${it.title}`
                    : `${it.numero ? it.numero + " · " : ""}${it.descripcion}`}
                </span>
              </button>
            ))}
          </div>
        )}
        <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.bmp,.heic,.heif,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.7z,.mp4,.mov,.mp3,.ogg,.m4a,.wav,.webm" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (!f) return; const limit = convo.type === "task" ? 15 : 30; if (f.size > limit * 1024 * 1024) { push(`El archivo supera el máximo de ${limit} MB para este canal.`, "error"); return; } setPending(f); }} />
        <button type="button" className="btn-ghost px-2" title="Adjuntar (imagen o documento)" onClick={() => fileRef.current?.click()}><Icon name="paperclip" size={18} /></button>
        <EmojiPicker onPick={(e) => setText((t) => t + e)} />
        <input className="input flex-1" placeholder={convo.type === "dm" || convo.type === "group" ? "Escribe un mensaje…" : "Escribe… @ persona · # proyecto · * tarea · ! requisición"} value={text} onChange={(e) => setText(e.target.value)}
          onPaste={(e) => {
            const it = [...(e.clipboardData?.items || [])].find((x) => x.type.startsWith("image/"));
            const blob = it?.getAsFile();
            if (blob) { const ext = (blob.type.split("/")[1] || "png").split("+")[0]; const file = new File([blob], `sticker-${Date.now()}.${ext}`, { type: blob.type }); const limit = convo.type === "task" ? 15 : 30; if (file.size > limit * 1024 * 1024) { push(`La imagen supera el máximo de ${limit} MB para este canal.`, "error"); return; } setPending(file); e.preventDefault(); }
          }} />
        <button className="btn-primary" disabled={sending} aria-label={sending ? "Enviando" : "Enviar mensaje"}>{sending ? <span className="text-xs">Enviando…</span> : <Icon name="send" size={16} />}</button>
      </form>
    </div>
  );
}

// Nuevo chat: DM 1-a-1 o grupo pequeño.
export function NewChatModal({ onClose, onOpened }) {
  const { user } = useAuth();
  const { push } = useToast();
  const [mode, setMode] = useState("dm");   // dm | group
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState([]);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/users").then((r) => setUsers((r.data || []).filter((u) => u.id !== user?.id && u.is_active))).catch(() => {}); }, [user]);
  const list = users.filter((u) => u.full_name.toLowerCase().includes(q.toLowerCase()));

  const startDm = async (u) => {
    setBusy(true);
    try { const { data } = await api.post(`/messaging/dm/${u.id}`); onOpened(data.conversation_id); onClose(); }
    catch (e) { push(e.response?.data?.detail || "No se pudo abrir", "error"); } finally { setBusy(false); }
  };
  const toggle = (id) => setPicked((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const createGroup = async () => {
    if (!title.trim() || picked.length === 0) { push("Nombre y al menos una persona", "error"); return; }
    setBusy(true);
    try { const { data } = await api.post("/messaging/groups", { title: title.trim(), member_ids: picked }); onOpened(data.conversation_id); onClose(); }
    catch (e) { push(e.response?.data?.detail || "No se pudo crear", "error"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="card w-full max-w-md p-4" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold">Nueva conversación</h3>
          <button className="btn-ghost px-2" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>
        <div className="mb-3 flex rounded-lg border border-slate-300 p-0.5 text-sm">
          <button className={`flex-1 rounded px-3 py-1 ${mode === "dm" ? "bg-brand-600 text-white" : "text-slate-600"}`} onClick={() => setMode("dm")}>Directo (1-a-1)</button>
          <button className={`flex-1 rounded px-3 py-1 ${mode === "group" ? "bg-brand-600 text-white" : "text-slate-600"}`} onClick={() => setMode("group")}>Grupo</button>
        </div>
        {mode === "group" && (
          <input className="input mb-2 w-full" placeholder="Nombre del grupo" value={title} onChange={(e) => setTitle(e.target.value)} />
        )}
        <input className="input mb-2 w-full" placeholder="Buscar persona…" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="max-h-64 space-y-1 overflow-y-auto">
          {list.map((u) => (
            mode === "dm" ? (
              <button key={u.id} onClick={() => startDm(u)} disabled={busy} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-slate-50">
                <Avatar name={u.full_name} size={22} /><span>{u.full_name}</span>
              </button>
            ) : (
              <label key={u.id} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50">
                <input type="checkbox" checked={picked.includes(u.id)} onChange={() => toggle(u.id)} />
                <Avatar name={u.full_name} size={22} /><span>{u.full_name}</span>
              </label>
            )
          ))}
        </div>
        {mode === "group" && (
          <div className="mt-3 flex justify-end">
            <button className="btn-primary text-sm" onClick={createGroup} disabled={busy}><Icon name="users" size={15} /> Crear grupo ({picked.length})</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Mensajeria() {
  const { features, can } = useAuth();
  const nav = useNavigate();
  const [convos, setConvos] = useState([]);
  const [sel, setSel] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [newChat, setNewChat] = useState(false);
  const [sp] = useSearchParams();
  const targetMessageId = sp.get("m");

  const loadConvos = useCallback(() => {
    api.get("/messaging/conversations").then((r) => setConvos(r.data.conversations || [])).catch(() => setConvos([]));
  }, []);
  useEffect(loadConvos, [loadConvos]);
  useEffect(() => {
    const refresh = () => loadConvos();
    window.addEventListener("tf:notification", refresh);
    window.addEventListener("tf:notifications-refresh", refresh);
    return () => { window.removeEventListener("tf:notification", refresh); window.removeEventListener("tf:notifications-refresh", refresh); };
  }, [loadConvos]);

  // Deep-link ?c=<conversation_id> abre esa DM/grupo.
  useEffect(() => {
    const cid = Number(sp.get("c"));
    const kind = sp.get("kind"), id = Number(sp.get("id"));
    if (!convos.length) return;
    const found = cid
      ? convos.find((c) => (c.conversation_id || (c.type === "dm" || c.type === "group" ? c.id : null)) === cid)
      : kind && id ? convos.find((c) => c.type === kind && Number(c.id) === id) : null;
    if (found) setSel(found);
  }, [sp, convos]);

  const openConversationId = (cid) => {
    loadConvos();
    setSel({ type: "dm", id: cid, conversation_id: cid, title: "Conversación", subtitle: "Mensaje directo" });
  };

  // Búsqueda inteligente en mensajes (con debounce).
  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(() => {
      api.get("/messaging/search", { params: { q: q.trim() } }).then((r) => setResults(r.data.results || [])).catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const shownConvos = q.trim()
    ? convos.filter((c) => `${c.title} ${c.subtitle}`.toLowerCase().includes(q.toLowerCase()))
    : convos;

  return (
    <div className={`flex min-h-0 overflow-hidden ${features?.soporte && can("soporte") ? "h-[calc(100dvh-112px)] sm:h-[calc(100dvh-56px)] md:h-[calc(100dvh-64px)]" : "h-[calc(100dvh-56px)] md:h-[calc(100dvh-64px)]"}`}>
      {/* Lista de conversaciones */}
      <aside className={`${sel ? "hidden md:flex" : "flex"} w-full shrink-0 flex-col border-r border-slate-200 bg-white md:w-80`}>
        <div className="border-b border-slate-200 p-3">
          <div className="mb-2 flex items-center justify-between">
            <h1 className="text-lg font-bold text-slate-800">Mensajería</h1>
            <button className="btn-outline-primary text-xs" onClick={() => setNewChat(true)}><Icon name="plus" size={14} /> Nuevo</button>
          </div>
          <div className="relative">
            <Icon name="search" size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
            <input className="input w-full pl-8" placeholder="Buscar conversaciones y mensajes…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {/* Resultados de búsqueda en mensajes */}
          {results && results.length > 0 && (
            <div className="border-b border-slate-100 bg-amber-50/40 p-2">
              <div className="mb-1 px-1 text-[11px] font-bold uppercase tracking-wide text-amber-600">Mensajes encontrados</div>
              {results.map((r, i) => (
                <button key={i} onClick={() => nav(r.link)} className="block w-full rounded-lg px-2 py-1.5 text-left hover:bg-white">
                  <div className="truncate text-xs font-medium text-slate-700">{r.title}</div>
                  <div className="truncate text-[11px] text-slate-500">{r.author}: {r.body}</div>
                </button>
              ))}
            </div>
          )}
          {results && results.length === 0 && q.trim().length >= 2 && (
            <p className="px-3 py-2 text-[11px] text-slate-400">Sin mensajes que coincidan.</p>
          )}
          {shownConvos.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-slate-400">No participas en conversaciones aún.</p>
          ) : shownConvos.map((c) => {
            const unread = c.unread > 0;
            const active = sel?.type === c.type && sel?.id === c.id;
            return (
            <button key={`${c.type}-${c.id}`} onClick={() => {
              setSel(c);
              // Abrir el hilo lo marca leído en el servidor: refleja el badge
              // al instante sin esperar a recargar toda la lista.
              if (c.unread > 0) {
                setConvos((arr) => arr.map((x) => x === c ? { ...x, unread: 0 } : x));
              }
            }}
              className={`relative flex w-full items-start gap-2.5 overflow-hidden border-b border-slate-50 py-2.5 pl-3 pr-3 text-left hover:bg-slate-50 ${active ? "bg-brand-50/60" : unread ? "bg-brand-50/30" : ""}`}>
              {/* Esquina con los mensajes nuevos: se ve de un vistazo cuántos
                  faltan por leer sin abrir la conversación. */}
              {unread && (
                <>
                  <span className="pointer-events-none absolute right-0 top-0 h-0 w-0 border-l-[34px] border-t-[34px] border-l-transparent border-t-rose-500" />
                  <span className="pointer-events-none absolute right-[3px] top-[1px] text-[11px] font-bold leading-none text-white">
                    {c.unread > 99 ? "99" : c.unread}
                  </span>
                </>
              )}
              <div className="relative mt-0.5 shrink-0">
                <Icon name={CONVO_ICON[c.type] || "message"} size={18} className={unread ? "text-brand-600" : "text-brand-500"} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className={`truncate text-sm ${unread ? "font-bold text-slate-900" : "font-semibold text-slate-800"}`}>{c.title}</span>
                  <span className={`shrink-0 pr-6 text-[11px] ${unread ? "font-semibold text-brand-600" : "text-slate-400"}`}>{c.last_at ? fmtStamp(c.last_at) : ""}</span>
                </div>
                {/* De qué proyecto y de qué tarea trata el hilo. */}
                {(c.proyecto || c.tarea) && (
                  <div className="flex min-w-0 items-center gap-1 text-[11px] text-slate-400">
                    {c.proyecto && (
                      <span className="truncate rounded bg-slate-100 px-1 font-medium text-slate-500">{c.proyecto}</span>
                    )}
                    {c.tarea && <span className="min-w-0 truncate">{c.proyecto ? "· " : ""}{c.tarea}</span>}
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <span className={`min-w-0 flex-1 truncate text-[11px] ${unread ? "font-medium text-slate-700" : "text-slate-500"}`}>{c.last_body || c.subtitle}</span>
                </div>
              </div>
            </button>
          ); })}
        </div>
      </aside>

      {newChat && <NewChatModal onClose={() => setNewChat(false)} onOpened={openConversationId} />}

      {/* Hilo */}
      <section className={`${sel ? "flex" : "hidden md:flex"} min-w-0 flex-1 bg-white`}>
        {sel ? <Thread convo={sel} onSent={loadConvos} onBack={() => setSel(null)} targetMessageId={targetMessageId} /> : (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-slate-400">
            <Icon name="message" size={40} />
            <p className="text-sm">Elige una conversación para seguir el hilo.</p>
            <p className="max-w-sm text-xs">Aquí se consolidan todos los chats de tus tareas y proyectos, como canales. Cada tarea/proyecto es un canal con su propio historial.</p>
          </div>
        )}
      </section>
    </div>
  );
}
